import React, {
  FC,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DataClassifiers } from '@ariksa/data-scanning/api';
import {
  Box,
  HStack,
  ListItem,
  OrderedList,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { map } from 'lodash';
import { customTheme } from 'theme';

import { PlusCountTag, Tag } from '../Tag';
import { formatTooltip } from '../Tooltip/utils';

import { renderDataTypeTag, renderTagLabel } from './render';
import { getDataTypeDisplayName, normalizeDataTypes } from './utils';

interface DataTypeTagsProps {
  dataTypes?: Record<string, any>[];
  classifiers?: DataClassifiers[];
}

const MAX_DATA_TYPE_ROWS = 2;
// Chakra Wrap puts its gap on an inner <ul> via `spacing` (default 0.5rem).
// `gap="4px"` only reached the outer div, so tags were really 8px apart while
// the layout maths assumed 4px, and narrow cells overflowed onto a third row.
const TAG_SPACING = '4px';

export const DataTypeTags: FC<DataTypeTagsProps> = ({
  dataTypes = [],
  classifiers = [],
}) => {
  const uniqueDataTypes = useMemo(
    () => Array.from(new Set(normalizeDataTypes(dataTypes))),
    [dataTypes],
  );
  const dataTypeDetails = useMemo(() => {
    const detailsByDataType: Record<string, Record<string, any>> = {};

    // Keep the complete data type record for display names and tooltip labels.
    classifiers.forEach(dataType => {
      const [name] = normalizeDataTypes(dataType);

      if (name) {
        detailsByDataType[name] = dataType;
      }
    });

    return detailsByDataType;
  }, [classifiers]);
  const containerRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLDivElement>(null);
  const measurementWrapRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(0);
  const [truncatedTag, setTruncatedTag] = useState<{
    index: number;
    maxTagWidth: number;
    maxTextWidth: number;
  } | null>(null);

  const measureVisibleCount = useCallback(() => {
    const container = containerRef.current;
    const measurement = measurementRef.current;
    const measurementWrap = measurementWrapRef.current;
    if (
      !container ||
      !measurement ||
      !measurementWrap ||
      !uniqueDataTypes.length
    )
      return;

    const containerWidth = container.clientWidth;
    if (!containerWidth) {
      // Keep data types visible while a table cell is still establishing its width.
      setVisibleCount(uniqueDataTypes.length);
      setTruncatedTag(null);
      return;
    }

    const tagElements = Array.from(
      measurement.querySelectorAll<HTMLElement>('[data-data-type-index]'),
    );
    const tagWidths = tagElements.map(element => element.offsetWidth);
    const tagTextWidths = tagElements.map(
      element =>
        element.querySelector<HTMLElement>('[data-data-type-label-index]')
          ?.offsetWidth ?? 0,
    );
    const plusWidths = new Map(
      Array.from(
        measurement.querySelectorAll<HTMLElement>('[data-plus-count]'),
      ).map(element => [
        Number(element.dataset.plusCount),
        element.offsetWidth,
      ]),
    );
    // Use the actual Wrap gap so the truncated tag reserves room for +N. It
    // lives on Wrap's inner list, not the outer element the ref points at.
    const list = measurementWrap.firstElementChild ?? measurementWrap;
    const columnGap = parseFloat(window.getComputedStyle(list).columnGap);
    const gap = Number.isNaN(columnGap) ? 0 : columnGap;

    const getLayout = (count: number) => {
      let rows = 1;
      let rowWidth = 0;
      const tagLayouts: {
        index: number;
        row: number;
        widthBefore: number;
      }[] = [];

      const placeItem = (width: number) => {
        if (rowWidth && rowWidth + gap + width > containerWidth) {
          rows += 1;
          rowWidth = 0;
        }

        const widthBefore = rowWidth;
        rowWidth += rowWidth ? gap + width : width;
        return { row: rows, widthBefore };
      };

      tagWidths.slice(0, count).forEach((width, index) => {
        tagLayouts.push({
          index,
          width,
          ...placeItem(width),
        });
      });

      const remainingCount = uniqueDataTypes.length - count;
      const plusWidth = plusWidths.get(remainingCount) ?? 0;
      const plusLayout = remainingCount > 0 ? placeItem(plusWidth) : null;

      return { rows, tagLayouts, plusLayout, plusWidth };
    };

    const getTruncation = (count: number) => {
      const layout = getLayout(count);
      if (
        !layout.plusLayout ||
        layout.plusLayout.row !== MAX_DATA_TYPE_ROWS + 1 ||
        layout.tagLayouts.some(tag => tag.row > MAX_DATA_TYPE_ROWS)
      ) {
        return null;
      }

      const lastSecondRowTag = [...layout.tagLayouts]
        .reverse()
        .find(tag => tag.row === MAX_DATA_TYPE_ROWS);
      if (!lastSecondRowTag) return null;

      const availableTagWidth =
        containerWidth -
        lastSecondRowTag.widthBefore -
        gap -
        layout.plusWidth -
        10;
      const nonTextWidth =
        tagWidths[lastSecondRowTag.index] -
        tagTextWidths[lastSecondRowTag.index];
      const maxTextWidth = availableTagWidth - nonTextWidth;

      return maxTextWidth > 0
        ? {
            index: lastSecondRowTag.index,
            maxTagWidth: availableTagWidth,
            maxTextWidth,
          }
        : null;
    };

    let nextVisibleCount = 0;
    let nextTruncatedTag: {
      index: number;
      maxTagWidth: number;
      maxTextWidth: number;
    } | null = null;
    for (let count = uniqueDataTypes.length; count >= 0; count -= 1) {
      const layout = getLayout(count);
      if (layout.rows <= MAX_DATA_TYPE_ROWS) {
        nextVisibleCount = count;
        break;
      }

      const truncation = getTruncation(count);
      if (truncation) {
        nextVisibleCount = count;
        nextTruncatedTag = truncation;
        break;
      }
    }

    setVisibleCount(currentCount =>
      currentCount === nextVisibleCount ? currentCount : nextVisibleCount,
    );
    setTruncatedTag(currentTruncation =>
      currentTruncation?.index === nextTruncatedTag?.index &&
      currentTruncation?.maxTagWidth === nextTruncatedTag?.maxTagWidth &&
      currentTruncation?.maxTextWidth === nextTruncatedTag?.maxTextWidth
        ? currentTruncation
        : nextTruncatedTag,
    );
  }, [uniqueDataTypes, dataTypeDetails]);

  useLayoutEffect(() => {
    measureVisibleCount();

    if (typeof ResizeObserver === 'undefined' || !containerRef.current) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(measureVisibleCount);
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [measureVisibleCount]);

  if (!uniqueDataTypes.length) {
    return <Text color={customTheme.colors.gray['250']}>-</Text>;
  }

  const visibleDataTypes = uniqueDataTypes.slice(0, visibleCount);
  const remainingCount = uniqueDataTypes.length - visibleDataTypes.length;
  const remaining = uniqueDataTypes.slice(visibleDataTypes.length);

  return (
    <Box ref={containerRef} position="relative" w="full">
      <Wrap spacing={TAG_SPACING}>
        {map(visibleDataTypes, (dataType, index) => {
          const isTruncated = truncatedTag?.index === index;
          const details = dataTypeDetails[dataType];

          return renderDataTypeTag(dataType, {
            classifiers,
            displayName:
              details?.read_name ??
              getDataTypeDisplayName(dataType, classifiers),
            labels: details?.labels,
            labelProps: isTruncated
              ? { width: truncatedTag.maxTextWidth }
              : undefined,
            tagStyles: isTruncated
              ? {
                  tag: {
                    width: truncatedTag.maxTagWidth,
                    maxW: truncatedTag.maxTagWidth,
                    minW: 0,
                    flexShrink: 0,
                  },
                }
              : undefined,
          });
        })}
        {remainingCount > 0 && (
          <PlusCountTag
            count={remainingCount}
            tooltip={formatTooltip({
              header: 'Data Types',
              content: (
                <OrderedList>
                  {map(remaining, o => (
                    <ListItem key={o}>
                      {dataTypeDetails[o]?.read_name ??
                        getDataTypeDisplayName(o, classifiers)}
                    </ListItem>
                  ))}
                </OrderedList>
              ),
              width: 64,
            })}
          />
        )}
      </Wrap>
      {/**
       * Render an invisible copy of all possible tags for measurement.
       * It must remain in the layout so offsetWidth returns real values,
       * while absolute positioning keeps it from affecting the visible tags.
       */}
      <Box
        ref={measurementRef}
        position="absolute"
        top={0}
        left={0}
        w="full"
        visibility="hidden"
        pointerEvents="none"
        aria-hidden
      >
        {/* Measure each display-name tag using the same wrapping and gap. */}
        <Wrap ref={measurementWrapRef} spacing={TAG_SPACING} w="full">
          {uniqueDataTypes.map((dataType, index) => (
            <WrapItem key={dataType} data-data-type-index={index}>
              {/* Measure with the same name and border as the visible tag
                  (read_name, no border); a narrower copy under-measured and
                  let the visible tags wrap onto a third line. */}
              <Tag
                label={renderTagLabel(dataType, {
                  classifiers,
                  displayName:
                    dataTypeDetails[dataType]?.read_name ??
                    getDataTypeDisplayName(dataType, classifiers),
                  labelIndex: index,
                })}
                styles={{
                  tag: {
                    border: 'none',
                    py: dataType === 'ADDRESS' ? '1.5px' : '3px',
                  },
                }}
              />
            </WrapItem>
          ))}
        </Wrap>

        {/**
         * Measure every possible overflow badge (+1, +2, ... +N).
         * The available space depends on the width of the final +N badge.
         */}
        <HStack position="absolute" top={0} left={0} spacing={0}>
          {uniqueDataTypes.map((_, index) => (
            <PlusCountTag
              key={index + 1}
              count={index + 1}
              data-plus-count={index + 1}
            />
          ))}
        </HStack>
      </Box>
    </Box>
  );
};
